import type { Sport } from '@h2h/shared';
import { Placeholder } from '../../../components/ui/Placeholder';
import { LayoutGrid } from '../../../components/ui/icons';
import { useCompetitionStandings, useCyclingStandings } from '../../../hooks/useQueries';
import { LEAGUE_TABLES } from '../../../data/leagueTable';
import { CompetitionStandingsView } from './CompetitionStandingsView';
import { CyclingStandingsView } from './CyclingStandingsView';
import { StaticTableView } from './StaticTableView';

// ── Table tab ────────────────────────────────────────────────────────────────-
// The real competition table (separate from the player's HeadToHead division).
// Football shows the league/group standings; cycling shows the race classifications
// (GC + jerseys + the latest stage finish) scraped from ProCyclingStats.
export function TableTab({
  leagueId,
  season,
  sport
}: {
  leagueId: string;
  season?: number;
  sport?: Sport;
}) {
  if (sport === 'CYCLING') return <CyclingTable leagueId={leagueId} />;
  return <FootballTable leagueId={leagueId} season={season} />;
}

function CyclingTable({ leagueId }: { leagueId: string }) {
  const { data: standings } = useCyclingStandings(leagueId);

  if (standings && standings.classifications.length > 0) {
    return <CyclingStandingsView standings={standings} />;
  }

  return (
    <Placeholder
      Icon={LayoutGrid}
      title="Race standings coming soon"
      body="The General classification and jersey tables appear here once the first stage finishes. Your HeadToHead division still updates every stage under Compete."
    />
  );
}

function FootballTable({ leagueId, season }: { leagueId: string; season?: number }) {
  const { data: standings } = useCompetitionStandings(leagueId);

  // Prefer the real provider standings (groups/table) when they've been ingested.
  if (standings && standings.groups.length > 0) {
    return <CompetitionStandingsView leagueId={leagueId} standings={standings} season={season} />;
  }

  // Static design-data snapshot for the leagues we shipped a table for.
  const table = LEAGUE_TABLES[leagueId];
  if (!table) {
    return (
      <Placeholder
        Icon={LayoutGrid}
        title="League table coming soon"
        body="The live standings feed for this competition isn’t available yet. The competition you’re ranked in still updates every round under Compete."
      />
    );
  }

  return <StaticTableView table={table} season={season} />;
}
